import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type {
  AllowedColSpan,
  DashboardItem,
  DashboardListItem
} from 'src/types/dashboard'

export type ItemEditorState =
  | { type: 'label'; itemId: string | null }
  | {
      type: 'chart'
      itemId: string | null
      queryId: string | null
      defaultTitle?: string
      defaultDescription?: string
    }
  | { type: 'image'; itemId: string | null }

interface DashboardState {
  draft: Partial<DashboardListItem>
  original: Partial<DashboardListItem> | null
  toastMessage: string | null
  itemEditor: ItemEditorState | null
}

const initialState: DashboardState = {
  draft: {},
  original: null,
  toastMessage: null,
  itemEditor: null
}

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    initNewDashboard(state) {
      state.draft = {}
      state.original = null
    },

    loadDashboard(state, action: PayloadAction<Partial<DashboardListItem>>) {
      state.draft = action.payload
      state.original = JSON.parse(JSON.stringify(action.payload))
    },

    updateDraftTitle(state, action: PayloadAction<string>) {
      state.draft.title = action.payload
    },

    togglePublic(state) {
      state.draft.public = !state.draft.public
    },

    addItem(state, action: PayloadAction<DashboardItem>) {
      if (!state.draft.items) state.draft.items = []
      state.draft.items.push(action.payload)
    },

    replaceItem(
      state,
      action: PayloadAction<{ itemId: string; item: DashboardItem }>
    ) {
      if (!state.draft.items) return
      const index = state.draft.items.findIndex(
        (i) => i.itemId === action.payload.itemId
      )
      if (index !== -1) state.draft.items[index] = action.payload.item
    },

    removeItem(state, action: PayloadAction<string>) {
      if (!state.draft.items) return
      state.draft.items = state.draft.items.filter(
        (i) => i.itemId !== action.payload
      )
    },

    resizeItem(
      state,
      action: PayloadAction<{ itemId: string; width: AllowedColSpan }>
    ) {
      const item = state.draft.items?.find(
        (i) => i.itemId === action.payload.itemId
      )
      if (item) item.width = action.payload.width
    },

    updateLayout(state, action: PayloadAction<DashboardItem[]>) {
      state.draft.items = action.payload
    },

    updateOriginalToDraft(state) {
      state.original = JSON.parse(JSON.stringify(state.draft))
    },

    showToast(state, action: PayloadAction<string>) {
      state.toastMessage = action.payload
    },

    clearToast(state) {
      state.toastMessage = null
    },

    openItemEditor(state, action: PayloadAction<ItemEditorState>) {
      state.itemEditor = action.payload
    },

    closeItemEditor(state) {
      state.itemEditor = null
    },

    resetDashboardState() {
      return initialState
    }
  }
})

export const {
  initNewDashboard,
  loadDashboard,
  updateDraftTitle,
  togglePublic,
  addItem,
  replaceItem,
  removeItem,
  resizeItem,
  updateLayout,
  updateOriginalToDraft,
  showToast,
  clearToast,
  openItemEditor,
  closeItemEditor,
  resetDashboardState
} = dashboardSlice.actions

export default dashboardSlice.reducer
